import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  getBabyById,
  getBabyEntitlement,
  getInvitations,
  getLatestAddedBaby,
} from "../api";
import { AuthContext } from "./AuthContext";

interface BabyContextState {
  selectedBabyId: string | null;
  updateSelectedBaby: (babyId: string | null) => {};
  selectedBaby: any;
  postAdded: boolean;
  setPostAdded: React.Dispatch<React.SetStateAction<boolean>>;
  refreshBabyContext: () => void;
  device: any;
  updateDevice: (device: any) => void;
  refreshGetDefaultBaby: () => void;
  hasInvitation: boolean;
  /* Whether the SELECTED BABY is paid for — not whether the signed-in account
     has paid. null means "not answered yet" and must never be read as unpaid:
     treating it as false flashes locks at a paying family on every switch. */
  babyHasActivePayment: boolean | null;
  /* Whether the signed-in account administers the selected baby. Only the
     admin can buy a subscription that unlocks it, so everyone else is told who
     to ask rather than being offered a purchase that would not help them. */
  isBabyAdmin: boolean | null;
  /* Who to ask, when the viewer cannot buy. null when there is nobody to name
     or nothing to say. */
  babyAdminName: string | null;
}

const BabyContext = createContext<BabyContextState | undefined>(undefined);

export const useBabyContext = () => {
  const context = useContext(BabyContext);
  if (!context) {
    throw new Error("useBabyContext must be used within a BabyProvider");
  }
  return context;
};

interface BabyProviderProps {
  children: ReactNode;
}

export const BabyProvider: React.FC<BabyProviderProps> = ({ children }) => {
  const [selectedBabyId, setSelectedBabyId] = useState<any>(
    localStorage.getItem("selected-baby")
  );
  const [selectedBaby, setSelectedBaby] = useState<any>();
  const [babyHasActivePayment, setBabyHasActivePayment] = useState<
    boolean | null
  >(null);
  const [isBabyAdmin, setIsBabyAdmin] = useState<boolean | null>(null);
  const [babyAdminName, setBabyAdminName] = useState<string | null>(null);
  const [postAdded, setPostAdded] = useState<boolean>(false);
  const [device, setDevice] = useState<any>();
  const { hasActivePayment } = React.useContext(AuthContext);
  const [hasInvitation, setHasInvitation] = useState<boolean>(false);

  useEffect(() => {
    const accessToken = localStorage.getItem("little-feather-token");
    if (selectedBabyId && accessToken) {
      handleGetBaby(selectedBabyId);
    } else if (accessToken) {
      getDefaultLatestBabyId();
    }

    // Set an interval to fetch invitations every 5 seconds
    const interval = setInterval(() => {
      if (selectedBabyId) {
        handleGetBabyInvitations(selectedBabyId);
      }
    }, 5000); // 5000ms = 5 seconds

    // Cleanup the interval when the component unmounts or `selectedBabyId` changes
    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBabyId]);

  const getDefaultLatestBabyId = async () => {
    if (window.location.pathname === "/add-baby") {
      return;
    }
    await getLatestAddedBaby().then(async ({ data }: any) => {
      if (data?.length === 0 || !data) {
        if (!hasActivePayment) {
          if (window.location.pathname === "/settings") {
            return;
          }
          window.location.href = "/settings";
          return;
        }
        // window.location.href = "/add-baby";
        return;
      }
      await updateSelectedBaby(data.id);
    });
  };

  const handleGetBaby = async (babyId: any) => {
    await getBabyById(babyId).then(({ data }) => {
      setSelectedBaby(data);
      // window.location.reload();
    });
    await handleGetBabyEntitlement(babyId);
    // await handleGetBabyInvitations(babyId);
  };

  /* The parent buys the journal; invited family contribute to it. So the
     question the UI has to ask is "is this baby paid for", not "has this
     account paid" — the latter showed an invited grandparent locks over a baby
     the parent was paying for, while the server accepted her writes. */
  const handleGetBabyEntitlement = async (babyId: any) => {
    if (!babyId) return;
    try {
      const { data } = await getBabyEntitlement(babyId);
      setBabyHasActivePayment(!!data?.activePayment);
      setIsBabyAdmin(!!data?.callerIsAdmin);
      setBabyAdminName(data?.adminName ?? null);
    } catch (error) {
      /* Keep the last known answer. Locking the app because one request failed
         would be worse than briefly trusting a stale yes. */
      console.error("Failed to read entitlement for baby:", error);
    }
  };

  const handleGetBabyInvitations = async (babyId: any) => {
    await getInvitations().then(({ data }) => {
      setHasInvitation(data.length > 0 ? true : false);
      // window.location.reload();
    });
  };

  const refreshBabyContext = () => {
    if (selectedBabyId) {
      handleGetBaby(selectedBabyId);
    }
  };

  const updateSelectedBaby = async (babyId: string | null) => {
    localStorage.removeItem("selected-baby");
    localStorage.setItem("selected-baby", babyId ?? "");

    /* Back to unknown: carrying the previous baby's answer across a switch
       would briefly unlock a baby nobody pays for, or offer a purchase to
       someone who cannot make it. */
    setBabyHasActivePayment(null);
    setIsBabyAdmin(null);
    setBabyAdminName(null);
    setSelectedBabyId(babyId);
    // window.location.reload();

    await handleGetBaby(babyId);
  };

  const updateDevice = (device: any) => {
    setDevice(device);
  };

  const refreshGetDefaultBaby = async () => {
    const storedBabyId = localStorage.getItem("selected-baby");
    await handleGetBaby(storedBabyId);
  };

  const contextValue: BabyContextState = {
    selectedBabyId,
    updateSelectedBaby,
    selectedBaby,
    postAdded,
    setPostAdded,
    refreshBabyContext,
    updateDevice,
    device,
    refreshGetDefaultBaby,
    hasInvitation,
    babyHasActivePayment,
    isBabyAdmin,
    babyAdminName,
  };

  return (
    <BabyContext.Provider value={contextValue}>{children}</BabyContext.Provider>
  );
};
